import React from 'react';
import { CreditCard, Banknote, CheckCircle } from 'lucide-react';

interface PaymentMethodSelectorProps {
  selectedMethod: 'cash_bank' | 'stripe';
  onMethodChange: (method: 'cash_bank' | 'stripe') => void;
  amount: number;
  className?: string;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  selectedMethod,
  onMethodChange,
  amount,
  className = '',
}) => {
  const formatted = `£${amount.toFixed(2)}`;

  const methods = [
    {
      id: 'cash_bank' as const,
      label: 'Cash or bank transfer',
      hint: `Pay ${formatted} before or on pickup — details follow by email.`,
      icon: <Banknote className="w-6 h-6" />,
    },
    {
      id: 'stripe' as const,
      label: 'Pay by card',
      hint: `${formatted} — secure checkout below.`,
      icon: <CreditCard className="w-6 h-6" />,
    },
  ];

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="grid md:grid-cols-2 gap-4">
        {methods.map((method) => {
          const active = selectedMethod === method.id;
          return (
            <button
              key={method.id}
              type="button"
              onClick={() => onMethodChange(method.id)}
              className={`relative text-left rounded-xl border-2 p-4 transition-all ${
                active
                  ? 'border-blue-500 bg-blue-50/80 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`p-2 rounded-lg shrink-0 ${
                    active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {method.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold text-gray-900">{method.label}</h4>
                    {active ? <CheckCircle className="w-5 h-5 text-blue-600 shrink-0" /> : null}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{method.hint}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentMethodSelector;
